import React, { useState } from 'react';
import DragDropLayout, { triggerHaptic } from './Layouts/DragDropLayout';

const PunctuationPoliceGame = ({ gameData, onComplete }) => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [userInput, setUserInput] = useState('');
    const [feedback, setFeedback] = useState(null); 
    const [showHint, setShowHint] = useState(false);

    const currentSentence = gameData?.sentences?.[currentIndex];
    const totalSentences = gameData?.sentences?.length || 0;
    const pointsPerSentence = gameData?.points_per_sentence || 15;

    const normalizeText = (text) => {
        if (!text) return '';
        return text.trim()
            .replace(/\s+/g, ' ') 
            .replace(/[„“”]/g, '"') 
            .replace(/[‘’]/g, "'") 
            .replace(/\s+([.,!?;:])/g, '$1') 
            .toLowerCase();
    };

    const handleSubmit = () => {
        if (!userInput.trim() || feedback) return;

        const userNormalized = normalizeText(userInput);
        const correctNormalized = normalizeText(currentSentence.correct);

        if (userNormalized === correctNormalized) {
            setFeedback('correct');
            const newScore = score + pointsPerSentence;
            setScore(newScore);
            triggerHaptic([100, 50, 100]);

            setTimeout(() => {
                setFeedback(null);
                setShowHint(false);
                setUserInput('');

                if (currentIndex < totalSentences - 1) {
                    setCurrentIndex(prev => prev + 1);
                } else {
                    triggerHaptic([200, 100, 200, 100, 300]);
                    onComplete?.(newScore);
                }
            }, 4000);
        } else {
            setFeedback('wrong');
            triggerHaptic([50, 100, 50]);
            setTimeout(() => setFeedback(null), 1500);
        }
    };

    if (!gameData || !gameData.sentences || !currentSentence) {
        return (
            <div className="premium-glass rounded-xl md:rounded-[3rem] p-4 md:p-12 text-center border-indigo-100 animate-pulse bg-white">
                <div className="text-6xl md:text-[100px] mb-8">👮</div>
                <h3 className="text-base md:text-3xl font-black text-indigo-900 tracking-tighter uppercase">Aktyvuojamas patrulis...</h3>
            </div>
        );
    }

    const progress = ((currentIndex + 1) / totalSentences) * 100;

    const hud = (
        <div className="premium-glass rounded-xl md:rounded-[2.5rem] p-3 md:p-8 border-white/40 shadow-2xl relative overflow-hidden bg-indigo-50">
            <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/5 rounded-full blur-[80px] pointer-events-none"></div>
            <div className="flex flex-col md:flex-row items-center justify-between gap-2 md:p-6 relative z-10">
                <div className="flex items-center gap-2 md:gap-4">
                    <div className="w-12 h-12 md:w-16 md:h-16 bg-indigo-100 rounded-xl md:rounded-2xl flex items-center justify-center text-xl md:text-3xl shadow-inner animate-float">
                        👮
                    </div>
                    <div>
                        <h3 className="text-base md:text-3xl font-black text-slate-800 tracking-tight leading-tight uppercase">
                            Skyrybos <span className="text-indigo-600">Policija</span>
                        </h3>
                    </div>
                </div>

                <div className="flex items-center gap-2 md:gap-4">
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Garbės Ženkleliai</p>
                        <p className="text-sm md:text-2xl font-black text-indigo-600 leading-none">⭐ {score}</p>
                    </div>
                    <div className="bg-white/50 px-4 py-2 md:px-6 md:py-3 rounded-xl md:rounded-2xl border border-white/50 shadow-inner text-center min-w-[80px] md:min-w-[100px]">
                        <p className="text-[8px] md:text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">Byla</p>
                        <p className="text-sm md:text-2xl font-black text-slate-800 leading-none">{currentIndex + 1}/{totalSentences}</p>
                    </div>
                </div>
            </div>

            <div className="mt-4 md:mt-6 w-full bg-slate-200/50 rounded-full h-2 md:h-3 p-0.5 md:p-1 shadow-inner overflow-hidden border border-slate-300">
                <div 
                    className="h-full bg-gradient-to-r from-indigo-400 via-blue-500 to-indigo-700 rounded-full transition-all duration-1000 shadow-[0_0_20px_rgba(99,102,241,0.4)]"
                    style={{ width: `${progress}%` }}
                />
            </div>
        </div>
    );

    const choices = (
        <div className="space-y-4 md:space-y-8 py-4 md:py-12">
            {/* Evidence Board (Sentence Display) */}
            <div className="premium-glass rounded-2xl md:rounded-[4rem] p-4 md:p-14 shadow-2xl border-white/60 bg-white/40 backdrop-blur-xl relative overflow-hidden flex flex-col items-center text-center">
                <div className="absolute top-0 right-0 p-4 md:p-12 text-6xl md:text-[150px] opacity-[0.03] select-none pointer-events-none rotate-12">👮</div>
                
                <div className="w-full max-w-5xl space-y-4 md:space-y-8 relative z-10">
                    <h4 className="text-[8px] md:text-[10px] font-black text-indigo-400 uppercase tracking-[0.5em] mb-2 md:mb-4">Analizuojamas Pažeidimas</h4>
                    
                    <div className="bg-slate-900 rounded-xl md:rounded-[3rem] p-6 md:p-14 shadow-[0_40px_80px_rgba(0,0,0,0.5)] border-4 md:border-8 border-slate-800 relative group overflow-hidden">
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.1)_0%,transparent_100%)] pointer-events-none" />
                        <p className="text-lg md:text-5xl font-black text-indigo-50 leading-tight tracking-tighter italic font-serif relative z-10">
                            „{currentSentence.incorrect}“
                        </p>
                    </div>
                </div>
            </div>

            {/* Hint Panel */}
            {showHint && (
                <div className="premium-glass rounded-2xl md:rounded-[3rem] p-4 md:p-10 border-amber-200 bg-amber-50/80 shadow-2xl animate-pop-in relative overflow-hidden flex flex-col md:flex-row items-center gap-4">
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-amber-100 rounded-xl md:rounded-2xl flex items-center justify-center text-3xl md:text-5xl shadow-inner border-2 border-amber-200 flex-shrink-0 animate-bounce">
                        🕵️
                    </div>
                    <div className="flex-1 text-center md:text-left">
                        <h5 className="text-[8px] md:text-[10px] font-black text-amber-600 uppercase tracking-[0.6em] mb-2">Instruktažas</h5>
                        <p className="text-sm md:text-2xl font-bold text-slate-800 leading-relaxed italic font-serif">
                            "{currentSentence.explanation}"
                        </p>
                    </div>
                </div>
            )}
        </div>
    );

    const targets = (
        <div className="space-y-4 md:space-y-8 w-full max-w-4xl mx-auto">
            <div className="relative group">
                <textarea
                    value={userInput}
                    onChange={(e) => setUserInput(e.target.value)}
                    placeholder="Ištaisyk skyrybą čia..."
                    disabled={!!feedback}
                    className="w-full p-4 md:p-10 text-sm md:text-3xl font-black bg-white rounded-xl md:rounded-[3rem] border-4 md:border-[6px] border-slate-100 focus:border-indigo-500 focus:outline-none shadow-xl transition-all placeholder:text-slate-200 disabled:opacity-50 min-h-[120px] md:min-h-[200px] text-slate-800 resize-none leading-relaxed"
                />
                <div className="absolute -top-4 md:-top-6 left-1/2 -translate-x-1/2 flex items-center gap-4 z-20">
                    <button
                        onClick={() => { triggerHaptic(20); setShowHint(!showHint); }}
                        className={`px-6 py-2 md:px-10 md:py-4 rounded-full flex items-center gap-2 text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all shadow-lg ${showHint ? 'bg-amber-500 text-white' : 'bg-slate-900 text-amber-400'}`}
                    >
                        {showHint ? '💡 SLĖPTI' : '🔦 PAGALBA'}
                    </button>
                </div>
            </div>

            <button
                onClick={handleSubmit}
                disabled={!userInput.trim() || !!feedback}
                className={`
                    premium-button-3d w-full py-4 md:py-10 bg-gradient-to-r from-indigo-600 to-indigo-800 text-white rounded-xl md:rounded-[3rem] font-black text-base md:text-3xl shadow-2xl transition-all border-b-8 md:border-b-[12px] border-indigo-900 uppercase tracking-widest
                    ${userInput.trim() && !feedback ? 'scale-[1.02] active:translate-y-2 active:border-b-0' : 'opacity-20'}
                `}
            >
                ✅ PATVIRTINTI TVARKĄ
            </button>
        </div>
    );

    return (
        <>
            <DragDropLayout 
                hud={hud}
                choices={choices}
                targets={targets}
            />

            {feedback && (
                <div className="fixed inset-0 flex items-center justify-center z-[2000] p-4">
                    <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-xl animate-fade-in" />
                    <div className={`
                        max-w-xl w-full p-6 md:p-12 rounded-[2.5rem] md:rounded-[4rem] bg-white shadow-2xl relative animate-pop-in border-[10px] md:border-[16px]
                        ${feedback === 'correct' ? 'border-emerald-500' : 'border-rose-500'}
                    `}>
                        <div className="absolute -top-10 left-1/2 -translate-x-1/2 w-20 h-20 bg-white rounded-full flex items-center justify-center text-4xl shadow-2xl border-4 border-inherit animate-bounce">
                            {feedback === 'correct' ? '🚓' : '🚨'}
                        </div>
                        
                        <div className="text-center space-y-4 pt-4">
                            <h2 className={`text-xl md:text-5xl font-black uppercase tracking-tighter ${feedback === 'correct' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                {feedback === 'correct' ? 'TIKSLU!' : 'KLAIDA!'}
                            </h2>

                            <div className={`p-4 md:p-10 rounded-xl md:rounded-[3rem] border-2 text-center italic font-serif leading-relaxed ${feedback === 'correct' ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'}`}>
                                <p className="text-sm md:text-2xl font-bold text-slate-700 leading-relaxed italic">
                                    {feedback === 'correct' ? 'Sakinys dabar yra visiškai taisyklingas. Puikus darbas!' : 'Oi! Panašu, kad sakinyje vis dar liko skyrybos pažeidimų.'}
                                </p>
                            </div>

                            {feedback === 'correct' && (
                                <div className="bg-slate-900 rounded-xl md:rounded-[2rem] p-4 md:p-6 text-white text-left">
                                    <p className="text-[8px] font-black text-emerald-400 uppercase tracking-widest mb-2">Teisingas Protokolas</p>
                                    <p className="text-xs md:text-xl font-black italic tracking-tight">„{currentSentence.correct}“</p>
                                </div>
                            )}

                            <div className="flex items-center justify-center gap-4">
                                <div className="flex gap-2">
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.2s]" />
                                    <div className="w-2 h-2 md:w-3 md:h-3 bg-slate-200 rounded-full animate-bounce [animation-delay:0.4s]" />
                                </div>
                                <span className="text-[10px] md:text-sm font-black text-slate-300 uppercase tracking-[0.3em]">
                                    {feedback === 'correct' ? 'Kita byla...' : 'Analizuojame...'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PunctuationPoliceGame;
